import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { Locale } from '../i18n/config';
import { getSettings, getMemberTypeByType } from '@/services/platform/data/cms';
import { ErrorBoundary, NoInternet } from '@/components/shared/errors';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { AppSettingsContentResponseModel } from '@/services/platform/api/model/appSettingsContentResponseModel';
import { setFavicon, getImageUrl, resolveThemeByLabel, DEFAULT_MEMBER_THEME } from '../utils';
import type { MemberTheme } from '../utils';
import { getCurrentUser, getMembership, getUserProfile } from '@/services/platform/data/profile';
import { useTokenRefresh } from '@/lib/core/hooks';
import { UserDto, MembershipDto, MeDto } from '@/services/platform/api/model';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';

interface AppContextProps {
  appSettings?: AppSettingsContentResponseModel;
  user?: UserDto;
  membership?: MembershipDto;
  profile?: MeDto;
  memberTheme: MemberTheme;
}
interface AppProviderProps {
  children: ReactNode;
  locale: Locale;
  onLocaleChange?: (locale: Locale) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider = ({ children, locale }: AppProviderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<AppSettingsContentResponseModel | undefined>(undefined);
  const [user, setUser] = useState<UserDto | undefined>(undefined);
  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);
  const [profile, setProfile] = useState<MeDto | undefined>(undefined);
  const [memberTheme, setMemberTheme] = useState<MemberTheme>(DEFAULT_MEMBER_THEME);
  const accessToken = useAuthStore(state => state.accessToken);
  useTokenRefresh();

  async function resolveMemberThemeFor(
    memberType: string | null | undefined,
    repositoryId: string | undefined,
    targetLocale: Locale
  ) {
    if (!memberType || !repositoryId) {
      setMemberTheme(DEFAULT_MEMBER_THEME);
      return;
    }
    const memberTypeContent = await getMemberTypeByType(repositoryId, memberType, targetLocale);
    setMemberTheme(resolveThemeByLabel(memberTypeContent?.properties?.approvedColor?.label));
  }

  const loadApp = useCallback(async (targetLocale: Locale) => {
    setIsLoading(true);
    try {
      // Getting Settings
      const settingsResponse = await getSettings(targetLocale);
      setSettings(settingsResponse);

      if (settingsResponse?.properties?.favicon) {
        setFavicon(getImageUrl(settingsResponse.properties.favicon?.[0]) || '');
      }

      // Getting User
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      if (currentUser) {
        if (currentUser?.hasActiveMembership) {
          // Getting Membership
          const membershipData = await getMembership();
          setMembership(membershipData);

          await resolveMemberThemeFor(
            membershipData?.type,
            settingsResponse?.properties?.memberTypeRepository?.id,
            targetLocale
          );
        }

        // Getting Profile
        const profileData = await getUserProfile();
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Failed to load app context:', error);
      setSettings(undefined);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApp(locale);
  }, [locale, loadApp]);

  // Re-fetch user profile and membership when a token arrives after the initial loadApp ran without one
  useEffect(() => {
    if (!accessToken || user) return;
    getCurrentUser()
      .then(u => {
        setUser(u);
        if (u) {
          if (u.hasActiveMembership) {
            getMembership()
              .then(async m => {
                setMembership(m);
                await resolveMemberThemeFor(m?.type, settings?.properties?.memberTypeRepository?.id, locale);
              })
              .catch(() => {});
          }
          getUserProfile()
            .then(p => setProfile(p))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [accessToken, user]);

  if (isLoading) {
    return <SpinLoading />;
  }

  return (
    <AppContext.Provider
      value={{
        appSettings: settings,
        user: user,
        membership: membership,
        profile: profile,
        memberTheme,
      }}
    >
      <ErrorBoundary>
        <NoInternet />
        {children}
      </ErrorBoundary>
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
